import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Truck, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierTask,
  type CarrierTrip,
  type CarrierTripDetail,
  useAssignTasksToTrip,
  useCarrierTripDetail,
  useCreateCarrierTrip,
} from "@/api/carrier";
import { ReadinessChip } from "@/features/dispatch/readiness-chip";
import { taskReferenceParts } from "@/lib/task-reference";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import {
  taskInstructionLabel,
  taskInstructions,
} from "@/lib/task-instructions";
import { dispatchTaskScheduleLabel } from "./dispatch-read-model";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { NativeSelect } from "@/components/ui/native-select";
import { operatorDisplayName } from "@/lib/operator-identity";
import {
  labelize,
  StatusPill,
  taskLabel,
  taskLocation,
  tripLabel,
} from "./dispatch-presenters";
import {
  compatibleAssignmentTrips,
  stagedDispatchCenterId,
} from "./task-staging";
import type { TaskFailureTarget } from "./task-failure";
import { TripRoutingWorkspace } from "./trip-routing-workspace";

const normalizeContact = (value?: string | null) =>
  value?.trim().replace(/\s+/g, " ").toLowerCase();

type BuildingVisitDraft = {
  id: number;
  taskIds: string[];
  waypointId: string;
  anchorTaskId: string;
  confirmed: boolean;
  confirmedReview?: BuildingVisitReview;
};

type BuildingVisitReview = {
  reviewedTaskIds: string[];
  fingerprint: string;
};

function captureBuildingVisitReview(
  visit: BuildingVisitDraft,
  tasks: CarrierTask[],
  targetTrip?: CarrierTripDetail
): BuildingVisitReview | null {
  const waypoint = visit.waypointId
    ? targetTrip?.waypoints.find((item) => item.id === visit.waypointId)
    : undefined;
  if (visit.waypointId && !waypoint) return null;
  const selected = visit.taskIds.map((id) =>
    tasks.find((task) => task.id === id)
  );
  if (selected.some((task) => !task)) return null;
  const anchorTask = tasks.find((task) => task.id === visit.anchorTaskId);
  const routingPin =
    waypoint?.locationKey ??
    (anchorTask?.mapLocation
      ? `${anchorTask.mapLocation.lat},${anchorTask.mapLocation.lng}`
      : null);
  if (!routingPin) return null;
  const existing = waypoint?.tasks ?? [];
  return {
    reviewedTaskIds: [...existing.map((task) => task.id), ...visit.taskIds],
    fingerprint: JSON.stringify({
      waypointId: visit.waypointId,
      anchorTaskId: visit.waypointId ? null : visit.anchorTaskId,
      routingPin,
      selected: selected.map((task) => [task?.id, task?.mapLocation]).sort(),
      existing: existing.map((task) => [task.id, task.mapLocation]).sort(),
    }),
  };
}

export function AssignPanel({
  tasks,
  trips,
  onRemoveTask,
  onClear,
  onClose,
  onAssigned,
  showHeader = true,
}: {
  tasks: CarrierTask[];
  trips: CarrierTrip[];
  onRemoveTask: (taskId: string) => void;
  onClear: () => void;
  onClose: () => void;
  onAssigned: () => void;
  showHeader?: boolean;
}) {
  const assign = useAssignTasksToTrip();
  const createTrip = useCreateCarrierTrip();
  const [target, setTarget] = useState("new");
  const [buildingVisits, setBuildingVisits] = useState<BuildingVisitDraft[]>(
    []
  );
  const [nextVisitId, setNextVisitId] = useState(1);
  const targetTrip = useCarrierTripDetail(target === "new" ? null : target);
  useEffect(() => {
    setBuildingVisits((current) => {
      let changed = false;
      const next = current.map((visit) => {
        if (
          !visit.confirmed ||
          visit.confirmedReview?.fingerprint ===
            captureBuildingVisitReview(visit, tasks, targetTrip.data)
              ?.fingerprint
        )
          return visit;
        changed = true;
        return { ...visit, confirmed: false, confirmedReview: undefined };
      });
      return changed ? next : current;
    });
  }, [tasks, targetTrip.data]);
  const submitLock = useRef(false);
  const createdTripId = useRef<string | null>(null);
  const dispatchCenterId = stagedDispatchCenterId(tasks);
  const openTrips = compatibleAssignmentTrips(trips, dispatchCenterId);
  const dispatchCenterName =
    tasks[0]?.dispatchCenter?.name ?? "Dispatch Center";

  const updateVisit = (
    id: number,
    update: (visit: BuildingVisitDraft) => BuildingVisitDraft
  ) =>
    setBuildingVisits((current) =>
      current.map((visit) => (visit.id === id ? update(visit) : visit))
    );

  const changeTarget = (value: string) => {
    setTarget(value);
    setBuildingVisits((current) =>
      current.map((visit) => ({ ...visit, waypointId: "", confirmed: false }))
    );
  };

  const removeTask = (taskId: string) => {
    setBuildingVisits((current) =>
      current.map((visit) => ({
        ...visit,
        taskIds: visit.taskIds.filter((id) => id !== taskId),
        anchorTaskId:
          visit.anchorTaskId === taskId
            ? (visit.taskIds.find((id) => id !== taskId) ?? "")
            : visit.anchorTaskId,
        confirmed: false,
      }))
    );
    onRemoveTask(taskId);
  };

  const handleAssign = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dispatchCenterId || tasks.length === 0 || submitLock.current) return;
    const selectedTaskIds = new Set(tasks.map((task) => task.id));
    const usedTaskIds = new Set<string>();
    const usedWaypointIds = new Set<string>();
    const validVisits = buildingVisits.every((visit) => {
      if (
        !visit.confirmedReview ||
        visit.confirmedReview.fingerprint !==
          captureBuildingVisitReview(visit, tasks, targetTrip.data)?.fingerprint
      )
        return false;
      if (
        visit.waypointId &&
        !targetTrip.data?.waypoints.some(
          (waypoint) => waypoint.id === visit.waypointId
        )
      )
        return false;
      if (
        !visit.confirmed ||
        visit.taskIds.length < (visit.waypointId ? 1 : 2) ||
        (!visit.waypointId && !visit.taskIds.includes(visit.anchorTaskId)) ||
        (visit.waypointId && target === "new")
      ) {
        return false;
      }
      if (visit.waypointId) {
        if (usedWaypointIds.has(visit.waypointId)) return false;
        usedWaypointIds.add(visit.waypointId);
      }
      for (const taskId of visit.taskIds) {
        if (!selectedTaskIds.has(taskId) || usedTaskIds.has(taskId))
          return false;
        usedTaskIds.add(taskId);
      }
      return true;
    });
    if (!validVisits) {
      toast.error("Confirm each building visit and select its tasks.");
      return;
    }
    submitLock.current = true;
    try {
      let tripId = target;
      if (target === "new") {
        if (!createdTripId.current) {
          const created = await createTrip.mutateAsync({ dispatchCenterId });
          createdTripId.current = created.id;
        }
        tripId = createdTripId.current;
      }
      await assign.mutateAsync({
        tripId,
        taskIds: tasks.map((task) => task.id),
        ...(buildingVisits.length
          ? {
              verifiedBuildingVisits: buildingVisits.map((visit) => ({
                taskIds: visit.taskIds,
                reviewedTaskIds: visit.confirmedReview?.reviewedTaskIds ?? [],
                ...(visit.waypointId ? { waypointId: visit.waypointId } : {}),
                ...(!visit.waypointId
                  ? { anchorTaskId: visit.anchorTaskId }
                  : {}),
                confirmed: true as const,
              })),
            }
          : {}),
      });
      toast.success(
        tasks.length === 1
          ? `${taskLabel(tasks[0])} assigned to trip.`
          : `${tasks.length} tasks assigned to trip.`
      );
      onAssigned();
    } catch {
      toast.error("Failed to assign task to trip.");
    } finally {
      submitLock.current = false;
    }
  };

  return (
    <form onSubmit={handleAssign} className="flex h-full flex-col">
      {showHeader && (
        <div className="flex h-14 items-center justify-between border-b px-4">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">
              Assign tasks
            </h2>
            <p className="text-xs text-zinc-500">{tasks.length} selected</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-11 lg:h-8"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs">
          <span className="font-semibold text-indigo-800">
            {dispatchCenterName} locked
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-11 px-3 text-xs lg:h-7 lg:px-2"
            onClick={() => {
              setBuildingVisits([]);
              onClear();
            }}
          >
            Clear selection
          </Button>
        </div>
        <div className="space-y-2">
          {tasks.map((task) => {
            const refs = taskReferenceParts(task);
            const stopContact = [task.contactName, task.contactCompany]
              .filter((value): value is string => Boolean(value?.trim()))
              .filter(
                (value, index, values) =>
                  values.findIndex(
                    (other) =>
                      normalizeContact(other) === normalizeContact(value)
                  ) === index &&
                  !task.shipment?.addresses?.some(
                    (address) =>
                      (address.type === "FROM" || address.type === "TO") &&
                      [address.contactName, address.contactCompany].some(
                        (party) =>
                          normalizeContact(party) === normalizeContact(value)
                      )
                  )
              )
              .map((value) => value.trim())
              .join(" · ");
            return (
              <div
                key={task.id}
                className="rounded-lg border border-zinc-200 bg-zinc-50 p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 break-words text-sm font-semibold leading-snug text-zinc-900">
                    {labelize(task.type)} ·{" "}
                    <span className="font-mono">{refs.task}</span>
                  </div>
                  <StatusPill status={task.status} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="h-11 w-11 lg:h-8 lg:w-8"
                    aria-label={`Remove ${taskLabel(task)}`}
                    onClick={() => removeTask(task.id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="mt-1 break-words font-mono text-[11px] text-zinc-500">
                  {refs.shipment}
                  {task.shipment?.orderNumber &&
                    ` · ORD-${task.shipment.orderNumber}`}
                </div>
                <div className="mt-1 break-words text-[13px] font-medium leading-snug text-zinc-900">
                  {taskLocation(task)}
                  {task.addressLine2?.trim() &&
                    ` · Unit ${task.addressLine2.trim()}`}
                </div>
                {taskInstructions(task) && (
                  <div className="mt-1 break-words text-xs text-amber-800">
                    {taskInstructionLabel(task)}: {taskInstructions(task)}
                  </div>
                )}
                {task.scheduledWindow?.startTime &&
                  task.scheduledWindow.endTime && (
                    <div className="mt-1 break-words text-xs font-medium text-amber-700">
                      {dispatchTaskScheduleLabel(task)}
                    </div>
                  )}
                {isTaskOnCancellationHold(task) && (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    On hold — cancellation review
                  </p>
                )}
                {task.type !== "CUSTOMER_DELIVERY" && task.notes?.trim() && (
                  <p className="mt-2 break-words rounded-md border border-amber-100 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                    {task.notes.trim()}
                  </p>
                )}
                <dl className="mt-2 space-y-1 text-xs">
                  {["FROM", "TO"].map((type) => {
                    const contact = task.shipment?.addresses?.find(
                      (address) => address.type === type
                    );
                    const name = contact?.contactName?.trim();
                    const company = contact?.contactCompany?.trim();
                    return (
                      <div key={type} className="flex gap-2">
                        <dt className="w-8 shrink-0 text-zinc-500">
                          {type === "FROM" ? "From" : "To"}
                        </dt>
                        <dd className="min-w-0 break-words text-zinc-700">
                          {name || "Not provided"}
                          {company && company !== name && (
                            <span className="block text-zinc-500">
                              {company}
                            </span>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
                {stopContact && (
                  <div className="mt-2 break-words text-xs text-zinc-700">
                    <span className="mr-2 text-zinc-500">Stop contact</span>
                    {stopContact}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {tasks.length > 0 && (
          <section
            className="space-y-2 border-t pt-3"
            aria-label="Building visits"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold">
                  Combined building visits
                </h3>
                <p className="text-xs text-zinc-500">
                  By default, Tasks at the exact same pin combine automatically.
                  Confirm a building visit here when different pins use the same
                  building and compatible access.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm-touch"
                onClick={() => {
                  setBuildingVisits((current) => [
                    ...current,
                    {
                      id: nextVisitId,
                      taskIds: [],
                      waypointId: "",
                      anchorTaskId: "",
                      confirmed: false,
                    },
                  ]);
                  setNextVisitId((id) => id + 1);
                }}
              >
                Add visit
              </Button>
            </div>
            {buildingVisits.map((visit, index) => (
              <fieldset
                key={visit.id}
                className="space-y-2 rounded-lg border p-3"
              >
                <legend className="text-xs font-semibold">
                  Building visit {index + 1}
                </legend>
                {tasks.map((task) => {
                  const inOtherVisit = buildingVisits.some(
                    (other) =>
                      other.id !== visit.id && other.taskIds.includes(task.id)
                  );
                  return (
                    <label
                      key={task.id}
                      htmlFor={`building-visit-task-${visit.id}-${task.id}`}
                      className="flex min-h-11 items-center gap-2 text-xs"
                    >
                      <Checkbox
                        id={`building-visit-task-${visit.id}-${task.id}`}
                        aria-label={`Add TSK-${task.taskNumber} to building visit ${index + 1}`}
                        checked={visit.taskIds.includes(task.id)}
                        disabled={inOtherVisit}
                        onCheckedChange={(checked) =>
                          updateVisit(visit.id, (current) => ({
                            ...current,
                            confirmed: false,
                            taskIds:
                              checked === true
                                ? [...current.taskIds, task.id]
                                : current.taskIds.filter(
                                    (id) => id !== task.id
                                  ),
                            anchorTaskId:
                              checked === true
                                ? current.anchorTaskId || task.id
                                : current.anchorTaskId === task.id
                                  ? (current.taskIds.find(
                                      (id) => id !== task.id
                                    ) ?? "")
                                  : current.anchorTaskId,
                          }))
                        }
                      />
                      {taskLabel(task)} · {taskLocation(task)}
                      {task.addressLine2?.trim() &&
                        ` · Unit ${task.addressLine2.trim()}`}
                      {task.contactName?.trim() &&
                        ` · ${task.contactName.trim()}`}
                      {taskInstructions(task) &&
                        ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
                      {task.mapLocation &&
                        ` · Pin ${task.mapLocation.lat}, ${task.mapLocation.lng}`}
                    </label>
                  );
                })}
                {!visit.waypointId && visit.taskIds.length > 0 && (
                  <label
                    htmlFor={`building-visit-pin-${visit.id}`}
                    className="block space-y-1 text-xs"
                  >
                    <span>Routing pin for this visit</span>
                    <NativeSelect
                      id={`building-visit-pin-${visit.id}`}
                      value={visit.anchorTaskId}
                      onChange={(event) =>
                        updateVisit(visit.id, (current) => ({
                          ...current,
                          anchorTaskId: event.target.value,
                          confirmed: false,
                        }))
                      }
                    >
                      {visit.taskIds.map((id) => {
                        const task = tasks.find((item) => item.id === id);
                        return (
                          <option key={id} value={id}>
                            TSK-{task?.taskNumber} ·{" "}
                            {task ? taskLocation(task) : "Unknown address"}
                          </option>
                        );
                      })}
                    </NativeSelect>
                  </label>
                )}
                {target !== "new" && (
                  <label
                    htmlFor={`building-visit-stop-${visit.id}`}
                    className="block space-y-1 text-xs"
                  >
                    <span>Add to existing stop, if this is the same visit</span>
                    <NativeSelect
                      id={`building-visit-stop-${visit.id}`}
                      value={visit.waypointId}
                      onChange={(event) =>
                        updateVisit(visit.id, (current) => ({
                          ...current,
                          waypointId: event.target.value,
                          confirmed: false,
                        }))
                      }
                    >
                      <option value="">Create a new stop</option>
                      {targetTrip.data?.waypoints
                        .filter(
                          (waypoint) =>
                            waypoint.tasks.length > 0 &&
                            waypoint.tasks.some(
                              (task) =>
                                !["COMPLETED", "FAILED", "CANCELLED"].includes(
                                  task.status
                                )
                            )
                        )
                        .map((waypoint) => (
                          <option key={waypoint.id} value={waypoint.id}>
                            Stop {waypoint.sequence} ·{" "}
                            {waypoint.tasks[0]?.addressLine1 ??
                              "Unknown address"}
                          </option>
                        ))}
                    </NativeSelect>
                  </label>
                )}
                {visit.waypointId && (
                  <div className="rounded bg-zinc-50 p-2 text-xs">
                    {targetTrip.data?.waypoints
                      .find((waypoint) => waypoint.id === visit.waypointId)
                      ?.tasks.map((task) => (
                        <div key={task.id}>
                          TSK-{task.taskNumber} ·{" "}
                          {`SHP-${task.shipmentNumber ?? "unknown"} · ${task.addressLine1 ?? "Unknown address"} · `}
                          {task.contactName ?? "No contact"}
                          {task.addressLine2 && ` · Unit ${task.addressLine2}`}
                          {taskInstructions(task) &&
                            ` · ${taskInstructionLabel(task)}: ${taskInstructions(task)}`}
                          {task.mapLocation &&
                            ` · Pin ${task.mapLocation.lat}, ${task.mapLocation.lng}`}
                        </div>
                      ))}
                  </div>
                )}
                <label
                  htmlFor={`building-visit-confirm-${visit.id}`}
                  className="flex min-h-11 items-center gap-2 text-xs"
                >
                  <Checkbox
                    id={`building-visit-confirm-${visit.id}`}
                    aria-label={`Confirm building visit ${index + 1}`}
                    checked={
                      visit.confirmed &&
                      visit.confirmedReview?.fingerprint ===
                        captureBuildingVisitReview(
                          visit,
                          tasks,
                          targetTrip.data
                        )?.fingerprint
                    }
                    onCheckedChange={(checked) =>
                      updateVisit(visit.id, (current) => {
                        const confirmedReview =
                          checked === true
                            ? captureBuildingVisitReview(
                                current,
                                tasks,
                                targetTrip.data
                              )
                            : null;
                        return {
                          ...current,
                          confirmed: Boolean(confirmedReview),
                          confirmedReview: confirmedReview ?? undefined,
                        };
                      })
                    }
                  />
                  I verified the same building and compatible entrance/access
                  for this visit.
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm-touch"
                  onClick={() =>
                    setBuildingVisits((current) =>
                      current.filter((item) => item.id !== visit.id)
                    )
                  }
                >
                  Remove visit
                </Button>
              </fieldset>
            ))}
          </section>
        )}
      </div>

      {/*
        Where the tasks are assigned TO does not scroll with the tasks. The
        list grows with the selection — five tasks was enough to push this
        control past the fold — and the thing you are about to press should
        never be the thing that scrolled away.
      */}
      <div className="space-y-3 border-t p-4">
        <label
          htmlFor="dispatch-target-trip"
          className="block space-y-1 text-sm"
        >
          <span className="font-medium">Target trip</span>
          <NativeSelect
            id="dispatch-target-trip"
            density="compact"
            className="h-11 md:h-9"
            value={target}
            onChange={(event) => changeTarget(event.target.value)}
            disabled={assign.isPending || createTrip.isPending}
          >
            <option value="new">New trip from selection</option>
            {openTrips.map((trip) => (
              <option key={trip.id} value={trip.id}>
                {tripLabel(trip)} ·{" "}
                {trip.operator
                  ? operatorDisplayName(trip.operator)
                  : "Unassigned"}{" "}
                · {labelize(trip.status)} ·{" "}
                {trip.dispatchCenter?.name ?? "No Dispatch Center"}
              </option>
            ))}
          </NativeSelect>
        </label>
        {openTrips.length === 0 && (
          <p className="text-sm text-zinc-500">
            No compatible planned or active Trips. Build a new Trip from this
            selection.
          </p>
        )}
        {assign.isError && (
          <p className="text-sm text-red-600">
            The task and trip must share a dispatch center.
          </p>
        )}
        <Button
          type="submit"
          className="h-11 w-full lg:h-9"
          disabled={
            !dispatchCenterId ||
            tasks.length === 0 ||
            assign.isPending ||
            createTrip.isPending
          }
        >
          {assign.isPending || createTrip.isPending ? (
            "Working..."
          ) : target === "new" ? (
            <>
              <Plus className="h-4 w-4" />
              Build into trip
            </>
          ) : (
            "Assign to trip"
          )}
        </Button>
      </div>
    </form>
  );
}

export function TripFocusModal({
  trip,
  open,
  onOpenChange,
  onFailTask,
}: {
  trip: CarrierTrip | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFailTask?: (task: TaskFailureTarget) => void;
}) {
  const detail = useCarrierTripDetail(trip?.id ?? null);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={trip ? `${tripLabel(trip)} focus` : "Trip focus"}
      bare
      className="flex h-[min(720px,92vh)] max-h-[92vh] w-[min(1120px,96vw)] max-w-[96vw] flex-col"
      presentation="responsive-sheet"
      responsiveSheetAt="tablet"
    >
      {!trip ? null : (
        <div className="flex h-full min-h-0 flex-col max-lg:[&_button]:min-h-11 max-lg:[&_input]:min-h-11">
          <TripIdentityStrip trip={trip} />

          {detail.isLoading && (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading stops...
            </div>
          )}
          {detail.error && (
            <div className="px-4 py-6 text-sm text-red-600">
              Failed to load stops.
            </div>
          )}

          {detail.data && (
            <TripRoutingWorkspace
              detail={detail.data}
              onFailTask={onFailTask}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Who and what this trip is, in one line.
 *
 * Replaces four bordered metric cards that spent a whole row restating the
 * dialog title. The trip number and "focus" are already in the dialog's own
 * heading, so this carries what the heading cannot: colour, status, the driver,
 * and whether the trip is ready to be worked.
 *
 * A trip with no driver says so in amber rather than reading "Unassigned" in
 * the same grey as everything else — an undriven trip is the single most
 * common reason a trip never reaches an operator.
 */
function TripIdentityStrip({ trip }: { trip: CarrierTrip }) {
  const color = trip.color ?? "#4f46e5";
  const driver = trip.operator ? operatorDisplayName(trip.operator) : null;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-zinc-200 px-4 py-3 pr-12">
      <span
        className="shrink-0 rounded"
        style={{ width: 5, height: 30, background: color }}
      />
      <div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[17px] font-bold text-zinc-900">
            {tripLabel(trip)}
          </span>
          <StatusPill status={trip.status} />
        </div>
        <div className="text-[11px] text-zinc-500">Focus trip</div>
      </div>

      <div className="mx-1 hidden h-9 w-px bg-zinc-200 sm:block" />

      {driver ? (
        <div className="flex items-center gap-2">
          <span
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full text-[11px] font-bold text-white"
            style={{ background: color }}
          >
            {driverInitials(driver)}
          </span>
          <div>
            <div className="text-[12.5px] font-bold text-zinc-900">
              {driver}
            </div>
            {trip.vehicle?.code && (
              <div className="flex items-center gap-1 text-[10.5px] text-zinc-500">
                <Truck className="h-3 w-3" />
                {trip.vehicle.code}
              </div>
            )}
          </div>
        </div>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-700">
          <UserPlus className="h-3.5 w-3.5" /> No driver assigned
        </span>
      )}

      <div className="flex-1" />
      <ReadinessChip trip={trip} />
    </div>
  );
}

function driverInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();
}
